import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ChevronDown, LayoutGrid, LayoutList, List, Plus, Maximize2, Loader2, Upload } from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { VoiceMemoDropZone } from './voice-memo-drop-zone';
import { VoiceMemoCard } from './voice-memo-card';
import { VoiceMemoListItem } from './voice-memo-list-item';
import { VoiceMemoSessionGroup, type VoiceMemoGroup } from './voice-memo-session-group';
import { getVoiceMemos, uploadVoiceMemos, processVoiceMemo, type VoiceMemo } from '@/lib/api';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import { SecondaryPanel } from '@/components/layout/secondary-panel';
import { VoiceMemoTranscriptPanel } from '@/components/integrations/voice-memos/voice-memo-transcript-panel';
import { VoiceMemoAnalysisPanel } from '@/components/integrations/voice-memos/voice-memo-analysis-panel';
import { PANEL_SIZES } from '@/lib/panel-sizes';
import { groupMemosBySession } from '@/utils/voice-memo-grouping';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type StatusFilter = 'all' | 'unprocessed' | 'transcribed' | 'analyzed' | 'filed' | 'ignored';
type SortType = 'recent' | 'oldest' | 'title-asc' | 'title-desc';
type ViewMode = 'card' | 'grid' | 'list' | 'tree';
type PanelContent = 'transcript' | 'analysis' | null;

export function VoiceMemoManager() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortType>('recent');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [viewMode, setViewMode] = usePersistedState<ViewMode>({
    key: StorageKeys.VOICE_MEMOS_VIEW_MODE,
    defaultValue: 'card',
    schema: StorageSchemas.viewMode,
  });

  // Panel state
  const [panelContent, setPanelContent] = useState<PanelContent>(null);
  const [selectedMemo, setSelectedMemo] = useState<VoiceMemo | null>(null);
  const [selectedMemoId, setSelectedMemoId] = useState<string | null>(null);
  const [transcribingFilenames, setTranscribingFilenames] = useState<Set<string>>(new Set());

  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Debug: Log panel state changes
  useEffect(() => {
    console.log('[VoiceMemoManager] Panel state changed:', {
      panelContent,
      selectedMemoId: selectedMemo?.id,
      selectedMemoFilename: selectedMemo?.filename,
      hasTranscriptPath: !!selectedMemo?.transcript?.path
    });
  }, [panelContent, selectedMemo]);

  const queryClient = useQueryClient();

  // Fetch voice memos
  const { data: voiceMemosResponse, isLoading, error } = useQuery({
    queryKey: ['voice-memos', statusFilter],
    queryFn: async () => {
      try {
        // For 'all', use 'unprocessed' tab which shows all non-ignored memos
        // (Backend doesn't have a true 'all' tab, but 'unprocessed' shows most memos)
        const tabToUse = statusFilter === 'all' ? 'unprocessed' : statusFilter;
        console.log('[VoiceMemoManager] Fetching with tab:', tabToUse, 'statusFilter:', statusFilter);

        const response = await getVoiceMemos({
          tab: tabToUse
        });

        console.log('[VoiceMemoManager] API response:', {
          success: response.success,
          total: response.total,
          memosCount: response.memos?.length || 0,
          counts: response.counts,
          firstMemo: response.memos?.[0]
        });

        return response;
      } catch (err) {
        console.error('[VoiceMemoManager] Query error:', err);
        throw err;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Extract memos from response
  const voiceMemosData = voiceMemosResponse?.memos || [];

  console.log('[VoiceMemoManager] Current state:', {
    isLoading,
    error: error?.message,
    statusFilter,
    memosCount: voiceMemosData.length,
    hasResponse: !!voiceMemosResponse,
    responseKeys: voiceMemosResponse ? Object.keys(voiceMemosResponse) : [],
    firstMemo: voiceMemosData[0]
  });

  // Get counts from response or calculate from memos
  const counts = useMemo(() => {
    if (voiceMemosResponse?.counts) {
      return {
        all: voiceMemosResponse.counts.total,
        unprocessed: voiceMemosResponse.counts.unprocessed,
        transcribed: 0, // Backend doesn't track this separately
        analyzed: voiceMemosResponse.counts.analyzed,
        filed: voiceMemosResponse.counts.filed,
        ignored: voiceMemosResponse.counts.ignored,
      };
    }
    if (!voiceMemosData.length) return { all: 0, unprocessed: 0, transcribed: 0, analyzed: 0, filed: 0, ignored: 0 };

    return {
      all: voiceMemosData.length,
      unprocessed: voiceMemosData.filter(m => m.status === 'unprocessed').length,
      transcribed: voiceMemosData.filter(m => m.status === 'transcribed').length,
      analyzed: voiceMemosData.filter(m => m.status === 'analyzed').length,
      filed: voiceMemosData.filter(m => m.status === 'filed').length,
      ignored: voiceMemosData.filter(m => m.status === 'ignored').length,
    };
  }, [voiceMemosResponse, voiceMemosData]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      return await uploadVoiceMemos(files);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['voice-memos'] });
      if (data.uploaded > 0) {
        toast.success(`Successfully uploaded ${data.uploaded} file${data.uploaded > 1 ? 's' : ''}`);
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} file${data.failed > 1 ? 's' : ''} failed to upload`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Upload failed: ${error.message}`);
    }
  });

  // Process mutation
  const processMutation = useMutation({
    mutationFn: async (filename: string) => {
      return await processVoiceMemo(filename, 'uploaded');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['voice-memos'] });
      toast.success(`${data.filename} transcribed successfully`);
      // Bridge to inbox so user sees item immediately (backend also creates via API)
      window.dispatchEvent(new CustomEvent('voice-memo-processed', {
        detail: {
          filename: data.filename,
          title: `Voice memo: ${data.filename}`,
          transcript: data.transcript?.path ? undefined : undefined,
        },
      }));
    },
    onError: (error: Error) => {
      toast.error(`Transcription failed: ${error.message}`);
    }
  });

  const handleFilesDropped = useCallback((files: File[]) => {
    uploadMutation.mutate(files);
  }, [uploadMutation]);

  const handleProcess = useCallback((filename: string) => {
    setTranscribingFilenames(prev => new Set(prev).add(filename));
    processMutation.mutate(filename, {
      onSettled: () => {
        setTranscribingFilenames(prev => {
          const next = new Set(prev);
          next.delete(filename);
          return next;
        });
      }
    });
  }, [processMutation]);

  const handleProcessAll = useCallback((filenames: string[]) => {
    // Process all memos in sequence with stagger
    filenames.forEach((filename, index) => {
      setTimeout(() => {
        handleProcess(filename);
      }, index * 500); // Stagger by 500ms
    });
  }, [handleProcess]);

  const handleViewTranscript = useCallback((memo: VoiceMemo) => {
    console.log('[VoiceMemoManager] handleViewTranscript called:', {
      memoId: memo.id,
      filename: memo.filename,
      hasTranscript: memo.hasTranscript,
      transcriptPath: memo.transcript?.path,
      transcript: memo.transcript
    });

    if (!memo.transcript?.path) {
      console.warn('[VoiceMemoManager] No transcript path available');
      toast.error('Transcript not available');
      return;
    }

    // Open transcript in right panel
    console.log('[VoiceMemoManager] Opening transcript panel');
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setPanelContent('transcript');
  }, []);

  const handleReviewAndFile = useCallback((memo: VoiceMemo) => {
    console.log('[VoiceMemoManager] handleReviewAndFile called:', {
      memoId: memo.id,
      filename: memo.filename,
      hasTranscript: memo.hasTranscript,
      transcriptPath: memo.transcript?.path,
      transcript: memo.transcript
    });

    // For "Review & File", open analysis in right panel (or transcript if analysis not available)
    if (!memo.transcript?.path) {
      console.warn('[VoiceMemoManager] No transcript path available for review');
      toast.error('Transcript not available');
      return;
    }

    // Try to open analysis, fallback to transcript
    console.log('[VoiceMemoManager] Opening analysis panel');
    setSelectedMemo(memo);
    setSelectedMemoId(memo.id);
    setPanelContent('analysis');
  }, []);

  const handleOpenInEditor = useCallback((filePath: string) => {
    // Open document in main editor
    window.dispatchEvent(new CustomEvent('open-document', {
      detail: {
        filePath,
        openInNewTab: false
      }
    }));
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelContent(null);
    setSelectedMemo(null);
    setSelectedMemoId(null);
  }, []);

  // Sort and filter memos, then group if in list view
  const sortedMemos = useMemo(() => {
    if (!voiceMemosData) return [];

    const sorted = [...voiceMemosData];

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        sorted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 'title-asc':
        sorted.sort((a, b) => a.filename.localeCompare(b.filename));
        break;
      case 'title-desc':
        sorted.sort((a, b) => b.filename.localeCompare(a.filename));
        break;
    }

    // Group by session if in list view
    if (viewMode === 'list') {
      return groupMemosBySession(sorted);
    }

    return sorted;
  }, [voiceMemosData, sortBy, viewMode]);

  const getStatusLabel = (status: StatusFilter) => {
    switch (status) {
      case 'unprocessed': return 'Unprocessed';
      case 'transcribed': return 'Transcribed';
      case 'analyzed': return 'Analyzed';
      case 'filed': return 'Filed';
      case 'ignored': return 'Ignored';
      default: return 'All Statuses';
    }
  };

  const getSortLabel = (sort: SortType) => {
    switch (sort) {
      case 'recent': return 'Most Recent';
      case 'oldest': return 'Oldest First';
      case 'title-asc': return 'Title (A-Z)';
      case 'title-desc': return 'Title (Z-A)';
    }
  };


  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-destructive mb-2">Error loading voice memos</p>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Split Panel Layout */}
      <PanelGroup direction="horizontal" className="flex-1 overflow-hidden">
        {/* LEFT PANEL - Voice Memo List */}
        <Panel
          defaultSize={panelContent ? 60 : 100}
          minSize={panelContent ? 40 : 100}
          maxSize={panelContent ? undefined : 100}
          className="overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-foreground">Voice Memos</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Focus view (hide sidebars)"
              title="Focus view (hide sidebars)"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              className="gap-2"
              onClick={() => setIsUploadModalOpen(true)}
              aria-label="Upload voice memos"
            >
              <Upload className="h-4 w-4" />
              Upload Files
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          {/* Status Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              aria-label={`Filter by status: ${getStatusLabel(statusFilter)}`}
              aria-expanded={showStatusMenu}
            >
              {getStatusLabel(statusFilter)} ({counts[statusFilter]})
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </Button>
            {showStatusMenu && (
              <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-10 min-w-[160px]">
                {(['all', 'unprocessed', 'transcribed', 'analyzed', 'filed', 'ignored'] as StatusFilter[]).map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setShowStatusMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                      statusFilter === status ? "bg-muted font-medium" : ""
                    }`}
                  >
                    {getStatusLabel(status)} ({counts[status]})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => setShowSortMenu(!showSortMenu)}
              aria-label={`Sort by: ${getSortLabel(sortBy)}`}
              aria-expanded={showSortMenu}
            >
              {getSortLabel(sortBy)}
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </Button>
            {showSortMenu && (
              <div className="absolute top-full mt-1 left-0 bg-background border border-border rounded-md shadow-lg z-10 min-w-[160px]">
                {(['recent', 'oldest', 'title-asc', 'title-desc'] as SortType[]).map((sort) => (
                  <button
                    key={sort}
                    onClick={() => {
                      setSortBy(sort);
                      setShowSortMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                      sortBy === sort ? "bg-muted font-medium" : ""
                    }`}
                  >
                    {getSortLabel(sort)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Active Filter Count */}
          {statusFilter !== 'all' && (
            <div className="text-xs text-muted-foreground ml-2">
              Showing {sortedMemos.length} of {counts.all}
            </div>
          )}

          {/* View Toggle - Button Group */}
          <div className="flex items-center border border-border rounded-md p-0.5 ml-auto" role="group" aria-label="View mode">
            <button
              onClick={() => setViewMode('card')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'card'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
              title="Card view"
              aria-pressed={viewMode === 'card'}
            >
              <LayoutList className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
              title="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-muted text-muted-foreground'
              }`}
              title="List view"
              aria-pressed={viewMode === 'list'}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-2" />
                <p className="text-muted-foreground">Loading voice memos...</p>
              </div>
            ) : sortedMemos.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No voice memos match your filters</p>
                <div className="max-w-md mx-auto">
                  <VoiceMemoDropZone
                    onFilesDropped={handleFilesDropped}
                    isUploading={uploadMutation.isPending}
                  />
                </div>
              </div>
            ) : (
              <>
                {/* Card View */}
                {viewMode === 'card' && (
                  <div className="p-6">
                    <div className="space-y-8">
                      {sortedMemos.map((item) => {
                        if ('memos' in item) {
                          // It's a group - show session header and all memos
                          return (
                            <div key={item.id}>
                              <div className="flex items-center gap-3 mb-4 px-2">
                                <div className="h-px flex-1 bg-border" />
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Session • {item.date} • {item.startTime.substring(0, 2)}:{item.startTime.substring(2, 4)} - {item.endTime.substring(0, 2)}:{item.endTime.substring(2, 4)} • {item.memos.length} memos
                                </div>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                {item.memos.map((memo) => (
                                  <VoiceMemoCard
                                    key={memo.id}
                                    memo={memo}
                                    onProcess={handleProcess}
                                    onViewTranscript={handleViewTranscript}
                                    onReviewAndFile={handleReviewAndFile}
                                    isSelected={selectedMemoId === memo.id}
                                    viewMode="card"
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // It's a single memo
                          return (
                            <VoiceMemoCard
                              key={item.id}
                              memo={item}
                              onProcess={handleProcess}
                              onViewTranscript={handleViewTranscript}
                              onReviewAndFile={handleReviewAndFile}
                              isSelected={selectedMemoId === item.id}
                              viewMode="card"
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="p-6">
                    <div className="space-y-8">
                      {sortedMemos.map((item) => {
                        if ('memos' in item) {
                          // It's a group - show session header and all memos
                          return (
                            <div key={item.id}>
                              <div className="flex items-center gap-3 mb-4 px-2">
                                <div className="h-px flex-1 bg-border" />
                                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Session • {item.date} • {item.startTime.substring(0, 2)}:{item.startTime.substring(2, 4)} - {item.endTime.substring(0, 2)}:{item.endTime.substring(2, 4)} • {item.memos.length} memos
                                </div>
                                <div className="h-px flex-1 bg-border" />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {item.memos.map((memo) => (
                                  <VoiceMemoCard
                                    key={memo.id}
                                    memo={memo}
                                    onProcess={handleProcess}
                                    onViewTranscript={handleViewTranscript}
                                    onReviewAndFile={handleReviewAndFile}
                                    isSelected={selectedMemoId === memo.id}
                                    viewMode="grid"
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // It's a single memo - show in grid
                          return (
                            <VoiceMemoCard
                              key={item.id}
                              memo={item}
                              onProcess={handleProcess}
                              onViewTranscript={handleViewTranscript}
                              onReviewAndFile={handleReviewAndFile}
                              isSelected={selectedMemoId === item.id}
                              viewMode="grid"
                            />
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <div className="p-2">
                    {sortedMemos.map((item) => {
                      if ('memos' in item) {
                        // It's a group
                        return (
                          <VoiceMemoSessionGroup
                            key={item.id}
                            group={item}
                            onProcess={handleProcess}
                            onProcessAll={handleProcessAll}
                            onViewTranscript={handleViewTranscript}
                            onReviewAndFile={handleReviewAndFile}
                            transcribingFilenames={transcribingFilenames}
                            selectedMemoId={selectedMemoId}
                          />
                        );
                      } else {
                        // It's a single memo
                        return (
                          <VoiceMemoListItem
                            key={item.id}
                            memo={item}
                            onProcess={handleProcess}
                            onViewTranscript={handleViewTranscript}
                            onReviewAndFile={handleReviewAndFile}
                            isSelected={selectedMemoId === item.id}
                          />
                        );
                      }
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hidden file input for upload button */}
          <input
            type="file"
            id="voice-memo-upload-input"
            multiple
            accept=".m4a,.m4b,.wav,.mp3,audio/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFilesDropped(Array.from(e.target.files));
                e.target.value = ''; // Reset input
              }
            }}
          />
        </Panel>

        {/* Resizable Handle - Only show when panel is open */}
        {panelContent && selectedMemo && (
          <>
            <PanelResizeHandle className="w-1 bg-primary/20 hover:bg-primary/30 transition-colors" />

            {/* RIGHT PANEL - Transcript or Analysis */}
            <SecondaryPanel
              panelType={panelContent === 'transcript' ? 'voice-memo-transcript' : 'voice-memo-analysis'}
              panelId={`voice-memo-${panelContent}-${selectedMemo.id}`}
              defaultSize={PANEL_SIZES.VOICE_MEMO_DEFAULT}
              minSize={PANEL_SIZES.VOICE_MEMO_MIN}
              className="border-l border-primary/20"
            >
              {panelContent === 'transcript' && selectedMemo && (
                <VoiceMemoTranscriptPanel
                  memo={selectedMemo}
                  onClose={handleClosePanel}
                  onOpenInEditor={handleOpenInEditor}
                />
              )}
              {panelContent === 'analysis' && selectedMemo && (
                <VoiceMemoAnalysisPanel
                  memo={selectedMemo}
                  onClose={handleClosePanel}
                  onOpenInEditor={handleOpenInEditor}
                />
              )}
            </SecondaryPanel>
          </>
        )}
      </PanelGroup>

      {/* Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Voice Memos</DialogTitle>
            <DialogDescription>
              Upload one or more audio files. Supported formats: .m4a, .m4b, .wav, .mp3 (max 25MB per file)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <VoiceMemoDropZone
              onFilesDropped={(files) => {
                handleFilesDropped(files);
                setIsUploadModalOpen(false);
              }}
              isUploading={uploadMutation.isPending}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
